import { useEffect, useState } from 'react';
import api from '@/api';
import { downloadAdminPurchaseOrderFile } from '@/services/portalPurchaseOrders';
import { Users, FileText, TrendingUp, Truck, Tag, MessageSquare, BarChart2, FileUp, Download, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CommodityBidAdminSection from '@/components/admin/CommodityBidAdminSection';

const StatCard = ({ label, value, sub, icon: Icon, color, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="bg-white rounded-xl border border-slate-200 p-5 text-left hover:shadow-md hover:border-slate-300 transition-all duration-200 group"
  >
    <div className="flex items-center justify-between mb-4">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-xs text-slate-400 font-medium group-hover:text-slate-600 transition-colors">View →</span>
    </div>
    <p className="font-heading text-2xl font-bold text-slate-900">{value ?? '—'}</p>
    <p className="text-sm font-medium text-slate-600 mt-0.5">{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
  </button>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentPo, setRecentPo] = useState([]);
  const [poDownloadId, setPoDownloadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [statsRes, poRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/purchase-orders'),
        ]);
        if (cancel) return;
        setStats(statsRes.data);
        const list = Array.isArray(poRes.data) ? poRes.data : [];
        setRecentPo(list.slice(0, 5));
      } catch (e) {
        if (!cancel) console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Admin Dashboard</h1>
        <p className="text-slate-500 text-sm mt-0.5">Overview of all platform activity</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-32 bg-white rounded-xl border animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard label="Total Customers" value={stats?.total_customers} sub={`${stats?.active_customers} active`} icon={Users} color="bg-slate-900" onClick={() => navigate('/admin/customers')} testId="stat-customers" />
            <StatCard label="Total RFQs" value={stats?.total_rfqs} sub={`${stats?.active_rfqs} active`} icon={FileText} color="bg-sky-600" onClick={() => navigate('/admin/rfqs')} testId="stat-rfqs" />
            <StatCard label="Commodity Requests" value={stats?.total_commodity} sub={`${stats?.active_commodity} active`} icon={TrendingUp} color="bg-orange-500" onClick={() => navigate('/admin/commodity')} testId="stat-commodity" />
            <StatCard label="Freight Requests" value={stats?.total_freight} sub={`${stats?.active_freight} active`} icon={Truck} color="bg-green-600" onClick={() => navigate('/admin/freight')} testId="stat-freight" />
            <StatCard label="Total Offers" value={stats?.total_offers} sub={`${stats?.pending_offers} pending`} icon={Tag} color="bg-violet-600" onClick={() => navigate('/admin/offers')} testId="stat-offers" />
            <StatCard label="Purchase orders" value={stats?.total_purchase_orders} sub="Müşteri PO yüklemeleri" icon={FileUp} color="bg-amber-600" onClick={() => navigate('/admin/purchase-orders')} testId="stat-purchase-orders" />
            <StatCard label="Unread Messages" value={stats?.unread_messages} icon={MessageSquare} color="bg-rose-500" onClick={() => {}} testId="stat-messages" />
          </div>

          {/* Recent purchase order uploads */}
          {recentPo.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <FileUp className="w-5 h-5 text-amber-600" />
                  <h2 className="font-heading text-base font-semibold text-slate-900">Son purchase order yüklemeleri</h2>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/admin/purchase-orders')}
                  className="text-sm font-medium text-amber-700 hover:text-amber-800"
                >
                  Tümünü gör →
                </button>
              </div>
              <ul className="divide-y divide-slate-50">
                {recentPo.map((row) => (
                  <li key={row._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{row.customer_name || '—'}</p>
                      <p className="text-xs text-slate-500 truncate">{row.file_name} · {row.customer_email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        setPoDownloadId(row._id);
                        try {
                          await downloadAdminPurchaseOrderFile(row._id, row.file_name);
                        } catch (err) {
                          console.error(err);
                        } finally {
                          setPoDownloadId(null);
                        }
                      }}
                      disabled={poDownloadId === row._id}
                      className="inline-flex items-center justify-center gap-1.5 shrink-0 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
                      data-testid={`dashboard-po-download-${row._id}`}
                    >
                      {poDownloadId === row._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      İndir
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CommodityBidAdminSection />

          {/* Platform Breakdown */}
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { name: 'DeMaxtore', total: stats?.total_rfqs, active: stats?.active_rfqs, color: 'bg-sky-500', text: 'text-sky-600' },
              { name: 'CommodityBid', total: stats?.total_commodity, active: stats?.active_commodity, color: 'bg-orange-500', text: 'text-orange-600' },
              { name: 'FreightIQ', total: stats?.total_freight, active: stats?.active_freight, color: 'bg-green-500', text: 'text-green-600' },
            ].map(p => (
              <div key={p.name} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-2 h-2 rounded-full ${p.color}`} />
                  <h3 className="font-heading text-base font-semibold text-slate-900">{p.name}</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={`font-heading text-xl font-bold ${p.text}`}>{p.total ?? 0}</p>
                    <p className="text-xs text-slate-400">Total Requests</p>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-bold text-slate-900">{p.active ?? 0}</p>
                    <p className="text-xs text-slate-400">Active</p>
                  </div>
                </div>
                <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${p.color} rounded-full transition-all`}
                    style={{ width: p.total > 0 ? `${(p.active / p.total) * 100}%` : '0%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
